import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Box, Button } from "@mui/material";
import FinishedOrders from "./finishedOrders";
import { orderObjectStatus } from "../../../constants/orderStatus";
import ReadyForBillOrders from "./readyForBillOrder";
import Totals from "../../orderDetailsForm/totals";
import { ErrorModal } from "../../../components/ModalAlert";
import { LoaderSpinner } from "../../../components/spinner/loader";
import supabase from "../../../supabase";
import moment from "moment";
import ClientInfo from "./clientInfo";
import { FormCard, FormActions, Field, TextInput } from "../../../components/ui/form";
import { createNewBill } from "../../../apiRequests/post";
import { logError } from "../../../utils/logger";
import { sendErrorNotification } from "../../../utils/emailSender";
import { jsPDF } from "jspdf";
import axios from "axios";
import { ticketObjectStatus } from "../../../constants/ticketStatus";
import { currencyValues } from "../../../constants/paymentValues";
import { updateSystemStatus, checkSystemStatus } from "./statusHandler";
import { sendEmailEndpoint } from "../../../apiRequests/endpoints";

// Función para obtener archivos de una orden (accesible desde generatePDF y el componente)
const getFiles = async (bucketName, orderId) => {
  const { data, error } = await supabase.storage
    .from(bucketName)
    .list(`${orderId}/`);
  if (error) throw error;
  return Promise.all(
    data.map((file) =>
      supabase.storage.from(bucketName).getPublicUrl(`${orderId}/${file.name}`)
    )
  ).then((urls) => urls.map(({ data }) => data.publicUrl));
};

export const generatePDF = async (orders, facturaNumero = null) => {
  const doc = new jsPDF();
  const pageWidth = doc.internal.pageSize.getWidth();
  const text = "Detalle de Ordenes de Trabajo";
  doc.setFontSize(16);

  const textWidth = doc.getTextWidth(text);
  const xPosition = (pageWidth - textWidth) / 2;
  doc.setFont("helvetica", "bold");
  doc.text(text, xPosition, 20);
  doc.setFont("helvetica", "normal");
  let yPosition = 40;

  const processedOrders = new Set();
  
  // Calcular el total de todas las órdenes
  let totalOrden = 0;
  const processedOrderIds = new Set();
  
  for (const order of orders) {
    if (!processedOrderIds.has(order.orderId)) {
      processedOrderIds.add(order.orderId);
      
      const orderSubtotal = order.orderDetails?.reduce((acc, detail) => {
        const currencyMultiplier = detail.currency !== currencyValues[0].value
          ? parseFloat(order.currencyRate)
          : 1;
        
        const detailPrice = parseFloat(detail.price) * parseFloat(detail.quantity) * currencyMultiplier;
        const detailIva105 = detail.ivaPercentage === 10.5 || detail.ivaPercentage === "10.5"
          ? detailPrice * 0.105
          : 0;
        const detailIva210 = detail.ivaPercentage === 21 || detail.ivaPercentage === "21"
          ? detailPrice * 0.21
          : 0;
        
        return acc + detailPrice + detailIva105 + detailIva210;
      }, 0) || 0;
      
      totalOrden += orderSubtotal;
    }
  }

  for (const order of orders) {
    if (!processedOrders.has(order.orderId)) {
      processedOrders.add(order.orderId);
      doc.setFontSize(14);
      doc.setFont("helvetica", "bold");
      doc.text(`Orden Nro: ${order.orderId}`, 20, yPosition);
      doc.setFont("helvetica", "normal");
      yPosition += 10;
      const date = new Date(order.createdAt);
      doc.setFontSize(12);
      doc.text(`Fecha: ${date.toLocaleDateString("es-ES")}`, 20, yPosition);
      yPosition += 10;
      if (facturaNumero) {
        doc.setFontSize(12);
        doc.text(`Factura Nro: ${facturaNumero}`, 20, yPosition);
        yPosition += 10;
      }
      order.orderDetails.forEach((detail) => {
        doc.setFontSize(12);
        const maxLineWidth = pageWidth - 40;
        const lines = doc.splitTextToSize(detail.description, maxLineWidth);
        if (yPosition + lines.length * 10 > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.text(lines, 20, yPosition);
        yPosition += lines.length * 5;
      });

      if (order.clientSignaturePhoto) {
        const imageWidth = 30;
        const imageHeight = 15;
        const imageX = 20;
        if (yPosition + imageHeight > 280) {
          doc.addPage();
          yPosition = 20;
        }
        doc.addImage(
          order.clientSignaturePhoto,
          "PNG",
          imageX,
          yPosition,
          imageWidth,
          imageHeight
        );
        yPosition += imageHeight + 4;
        doc.setFontSize(12);
        doc.text(`${order.clientSignatureName}`, 20, yPosition);
        yPosition += 10;

        if (yPosition > 280) {
          doc.addPage();
          yPosition = 20;
        }
      }

      if (order.bucketId) {
        const files = await getFiles(order.bucketId, order.orderId);

        if (files.length > 0) {
          let column = 0;
          let maxImageHeightInRow = 0;
          for (let i = 0; i < files.length; i++) {
            const photoUrl = files[i];
            if (photoUrl) {
              const imageWidth = 80;
              const imageHeight = 50;
              const imageX = column === 0 ? 20 : 110;
              if (yPosition + imageHeight > 280) {
                doc.addPage();
                yPosition = 20;
                maxImageHeightInRow = 0;
              }
              doc.addImage(
                photoUrl,
                "JPEG",
                imageX,
                yPosition,
                imageWidth,
                imageHeight
              );
              if (imageHeight > maxImageHeightInRow) {
                maxImageHeightInRow = imageHeight;
              }
              if (column === 1 || i === files.length - 1) {
                yPosition += maxImageHeightInRow + 10;
                maxImageHeightInRow = 0;
              }
              column = (column + 1) % 2;
            }
          }
        }
      }
      
      // Agregar el total de la orden actual
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      const orderSubtotal = order.orderDetails?.reduce((acc, detail) => {
        const currencyMultiplier = detail.currency !== currencyValues[0].value
          ? parseFloat(order.currencyRate)
          : 1;
        
        const detailPrice = parseFloat(detail.price) * parseFloat(detail.quantity) * currencyMultiplier;
        const detailIva105 = detail.ivaPercentage === 10.5 || detail.ivaPercentage === "10.5"
          ? detailPrice * 0.105
          : 0;
        const detailIva210 = detail.ivaPercentage === 21 || detail.ivaPercentage === "21"
          ? detailPrice * 0.21
          : 0;
        
        return acc + detailPrice + detailIva105 + detailIva210;
      }, 0) || 0;
      
      doc.text(`Total Orden: $ ${orderSubtotal.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 20, yPosition);
      yPosition += 10;
      
      doc.text(
        `____________________________________________________________________________`,
        20,
        yPosition
      );
      yPosition += 10;
    }
  }
  
  // Agregar el total general de todas las órdenes al final del PDF
  if (yPosition > 250) {
    doc.addPage();
    yPosition = 20;
  }
  
  doc.setFontSize(14);
  doc.setFont("helvetica", "bold");
  doc.text(`TOTAL GENERAL: $ ${totalOrden.toLocaleString('es-AR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 20, yPosition);
  yPosition += 15;
  
  doc.setFont("helvetica", "normal");
  doc.setFontSize(12);

  return doc.output("datauristring");
};

const ClientSpecificOrdersTable = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const data = location?.state;
  const [finishedOrders, setFinishedOrders] = useState([]);
  const [selectedOrdersForFacturation, setSelectedOrdersForFacturation] =
    useState([]);
  const [client, setClient] = useState({});
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [readyForBillOrders, setReadyForBillOrders] = useState([]);
  const [totalExpenses, setTotalExpenses] = useState(0);
  const [totalExpensesWithIva, setTotalExpensesWithIva] = useState(0);
  const [totalIva105, setTotalIva105] = useState(0);
  const [totalIva210, setTotalIva210] = useState(0);
  // Función para obtener la fecha por defecto de vencimiento (hoy + 31 días) en formato YYYY-MM-DD
  // Usa componentes locales para evitar problemas de zona horaria
  const getDefaultExpirationDate = () => {
    const today = new Date();
    // Usar componentes locales
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    
    // Crear fecha con componentes locales y sumar 31 días
    const expirationDate = new Date(year, month, day + 31, 0, 0, 0, 0);
    
    const expYear = expirationDate.getFullYear();
    const expMonth = String(expirationDate.getMonth() + 1).padStart(2, "0");
    const expDay = String(expirationDate.getDate()).padStart(2, "0");
    
    return `${expYear}-${expMonth}-${expDay}`;
  };

  const [customExpirationDate, setCustomExpirationDate] = useState(() => getDefaultExpirationDate());
  const [expirationDateError, setExpirationDateError] = useState("");

  const resetTotals = () => {
    setTotalExpenses(0);
    setTotalExpensesWithIva(0);
    setTotalIva105(0);
    setTotalIva210(0);
  };

  const calculateTotals = (orders) => {
    return orders.reduce(
      (acc, item) => {
        const orderSubtotals = item.orderDetails?.reduce(
          (detailAcc, detail) => {
            const currencyMultiplier =
              detail.currency !== currencyValues[0].value
                ? parseFloat(item.currencyRate)
                : 1;

            const detailPrice =
              parseFloat(detail.price) *
              parseFloat(detail.quantity) *
              currencyMultiplier;
            const detailIva105 =
              detail.ivaPercentage === 10.5 || detail.ivaPercentage === "10.5"
                ? detailPrice * 0.105
                : 0;
            const detailIva210 =
              detail.ivaPercentage === 21 || detail.ivaPercentage === "21"
                ? detailPrice * 0.21
                : 0;

            return {
              subTotal: detailAcc.subTotal + detailPrice,
              iva105: detailAcc.iva105 + detailIva105,
              iva210: detailAcc.iva210 + detailIva210,
            };
          },
          { subTotal: 0, iva105: 0, iva210: 0 }
        ) || {
          subTotal: item.subTotal,
          iva105: item.iva105,
          iva210: item.iva210,
        };

        return {
          finalPrice: acc.finalPrice + orderSubtotals.subTotal,
          finalPriceWithIva:
            acc.finalPriceWithIva +
            orderSubtotals.subTotal +
            orderSubtotals.iva105 +
            orderSubtotals.iva210,
          finalPriceIva105: acc.finalPriceIva105 + orderSubtotals.iva105,
          finalPriceIva21: acc.finalPriceIva21 + orderSubtotals.iva210,
        };
      },
      {
        finalPrice: 0,
        finalPriceWithIva: 0,
        finalPriceIva105: 0,
        finalPriceIva21: 0,
      }
    );
  };

  const getOrders = async () => {
    setLoading(true);
    try {
      // Cargar datos básicos de órdenes primero
      const { data: ordersData, error } = await supabase
        .from("orders")
        .select("orderId, clientId, status, createdAt, bucketId, currencyRate, subTotal, iva105, iva210")
        .eq("clientId", data?.client.clientId)
        .in("status", [
          orderObjectStatus.terminado,
          orderObjectStatus.listoParaFacturar,
        ])
        .or("ticketId.is.null,ticketId.eq.1");

      if (error) throw error;

      // Separar órdenes por estado para optimizar la carga
      const finishedOrdersBasic = ordersData.filter(
        (order) => order.status === orderObjectStatus.terminado
      );
      const readyForBillOrdersBasic = ordersData.filter(
        (order) => order.status === orderObjectStatus.listoParaFacturar
      );

      // Calcular totales básicos sin cargar detalles completos
      const totals = calculateTotals(readyForBillOrdersBasic);
      setClient({ ...data?.client, ...totals });

      // Mostrar datos básicos inmediatamente
      const basicFinishedOrders = finishedOrdersBasic.map((order) => ({
        ...order,
        description: "Cargando...",
        date: moment(order.createdAt).format("DD/MM/YY"),
        time: moment(order.createdAt).format("HH:mm:ss"),
        files: [],
      }));

      const basicReadyForBillOrders = readyForBillOrdersBasic.map((order) => ({
        ...order,
        description: "Cargando...",
        date: moment(order.createdAt).format("DD/MM/YY"),
        time: moment(order.createdAt).format("HH:mm:ss"),
        files: [],
      }));

      setFinishedOrders(basicFinishedOrders);
      setReadyForBillOrders(basicReadyForBillOrders);

      // Cargar detalles en segundo plano con UNA sola consulta y sin fotos
      const loadOrderDetails = async () => {
        setLoadingDetails(true);
        try {
          const allOrderIds = [
            ...finishedOrdersBasic.map(o => o.orderId),
            ...readyForBillOrdersBasic.map(o => o.orderId)
          ];

          if (allOrderIds.length === 0) {
            setLoadingDetails(false);
            return;
          }

          // Traer TODOS los detalles en una sola llamada y solo columnas necesarias
          const { data: allDetails, error: allDetailsError } = await supabase
            .from("orderDetails")
            .select("orderId, description, price, quantity, ivaPercentage, currency, createdAt, orderDetailsId")
            .in("orderId", allOrderIds);

          if (allDetailsError) {
            console.error("Error obteniendo detalles de órdenes:", allDetailsError);
          }

          // Agrupar detalles por orderId
          const detailsByOrderId = new Map();
          (allDetails || []).forEach(d => {
            if (!detailsByOrderId.has(d.orderId)) detailsByOrderId.set(d.orderId, []);
            detailsByOrderId.get(d.orderId).push(d);
          });

          // Mapear órdenes agregando detalles y evitando cargar fotos de storage
          const finishedOrdersWithDetails = finishedOrdersBasic.map(order => {
            const orderDetailsData = detailsByOrderId.get(order.orderId) || [];
            return {
              ...order,
              orderDetails: orderDetailsData,
              description: orderDetailsData?.[0]?.description || "Sin descripción",
              date: order.createdAt ? moment(order.createdAt).format("DD/MM/YY") : "N/A",
              time: order.createdAt ? moment(order.createdAt).format("HH:mm:ss") : "N/A",
              files: [], // Fotos se cargan on-demand (evitamos llamadas a storage aquí)
            };
          });

          const readyForBillOrdersWithDetails = readyForBillOrdersBasic.map(order => {
            const orderDetailsData = detailsByOrderId.get(order.orderId) || [];
            return {
              ...order,
              orderDetails: orderDetailsData,
              description: orderDetailsData?.[0]?.description || "Sin descripción",
              date: order.createdAt ? moment(order.createdAt).format("DD/MM/YY") : "N/A",
              time: order.createdAt ? moment(order.createdAt).format("HH:mm:ss") : "N/A",
              files: [],
            };
          });

          setFinishedOrders(finishedOrdersWithDetails);
          setReadyForBillOrders(readyForBillOrdersWithDetails);

          // Recalcular totales con datos completos
          const updatedTotals = calculateTotals(readyForBillOrdersWithDetails);
          setClient({ ...data?.client, ...updatedTotals });
        } catch (error) {
          console.error("Error cargando detalles de órdenes:", error);
        } finally {
          setLoadingDetails(false);
        }
      };

      // Ejecutar carga de detalles en segundo plano
      loadOrderDetails();
    } catch (error) {
      ErrorModal({ title: error.message });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getOrders();
  }, [data?.client]);

  const getLocalDate = (isCurrent) => {
    const currentDate = new Date();
    const day = String(currentDate.getDate()).padStart(2, "0");
    const month = String(
      isCurrent ? currentDate.getMonth() + 2 : currentDate.getMonth() + 1
    ).padStart(2, "0");
    const year = currentDate.getFullYear();
    return `${day}/${month}/${year}`;
  };

  // Función auxiliar para formatear fecha personalizada en formato dd/mm/yyyy
  const formatCustomDate = (date) => {
    if (!date) return null;
    
    // Asegurarse de que date sea un string válido
    if (typeof date !== 'string' && typeof date !== 'number') {
      return null;
    }
    
    const dateStr = String(date);
    
    // Parsear la fecha directamente desde el string YYYY-MM-DD para evitar problemas de zona horaria
    const dateParts = dateStr.split('-');
    if (dateParts.length === 3) {
      const year = dateParts[0];
      const month = dateParts[1];
      const day = dateParts[2];
      
      // Validar que los valores sean numéricos válidos
      if (isNaN(year) || isNaN(month) || isNaN(day)) {
        return null;
      }
      
      // Validar rangos básicos
      if (parseInt(month) < 1 || parseInt(month) > 12 || parseInt(day) < 1 || parseInt(day) > 31) {
        return null;
      }
      
      return `${day}/${month}/${year}`;
    }
    
    // Fallback: si el formato no es el esperado, usar Date (aunque puede tener problemas de zona horaria)
    const dateObj = new Date(dateStr);
    
    // Validar que la fecha sea válida
    if (isNaN(dateObj.getTime())) {
      return null;
    }
    
    const day = String(dateObj.getDate()).padStart(2, "0");
    const month = String(dateObj.getMonth() + 1).padStart(2, "0");
    const year = dateObj.getFullYear();
    
    // Validar que no haya valores NaN en el resultado
    if (day === 'NaN' || month === 'NaN' || isNaN(year)) {
      return null;
    }
    
    return `${day}/${month}/${year}`;
  };

  // Función para obtener la fecha de hoy en zona horaria local (solo fecha, sin hora)
  const getTodayLocalDate = () => {
    const today = new Date();
    // Usar métodos que devuelven valores en zona horaria local
    const year = today.getFullYear();
    const month = today.getMonth();
    const day = today.getDate();
    // Crear nueva fecha con componentes locales para evitar problemas de zona horaria
    return new Date(year, month, day, 0, 0, 0, 0);
  };

  // Función para validar que la fecha de vencimiento sea mayor o igual a la fecha actual
  const validateExpirationDate = (dateString) => {
    if (!dateString) {
      setExpirationDateError("");
      return true;
    }
    
    // Parsear la fecha desde el string YYYY-MM-DD usando componentes locales
    const dateParts = dateString.split('-');
    if (dateParts.length !== 3) {
      setExpirationDateError("La fecha de vencimiento no tiene un formato válido");
      return false;
    }
    
    const year = parseInt(dateParts[0], 10);
    const month = parseInt(dateParts[1], 10) - 1; // Los meses en Date son 0-indexed
    const day = parseInt(dateParts[2], 10);
    
    // Crear fecha usando componentes locales
    const selectedDate = new Date(year, month, day, 0, 0, 0, 0);
    
    // Validar que la fecha sea válida
    if (isNaN(selectedDate.getTime())) {
      setExpirationDateError("La fecha de vencimiento no es válida");
      return false;
    }
    
    const today = getTodayLocalDate();
    
    if (selectedDate < today) {
      setExpirationDateError("La fecha de vencimiento debe ser mayor o igual a la fecha actual");
      return false;
    }
    
    setExpirationDateError("");
    return true;
  };

  const sendEmail = async (pdfBase64, facturaNumero = null) => {
    // Usar emailBilling como principal (consistente con tusfacturas.app), con fallback a email
    const primaryEmail = client.emailBilling || client.email;
    
    // Validar que el cliente tenga email configurado
    if (!primaryEmail || primaryEmail.trim() === '') {
      throw new Error("El cliente no tiene un email para facturación configurado. Por favor, actualice los datos del cliente (emailBilling o email).");
    }

    // Validar formato de email principal
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(primaryEmail)) {
      throw new Error(`El email para facturación del cliente (${primaryEmail}) no tiene un formato válido.`);
    }

    // Validar email secundario si existe
    if (client.secondaryEmailBilling && client.secondaryEmailBilling.trim() !== '' && !emailRegex.test(client.secondaryEmailBilling)) {
      throw new Error(`El email secundario (${client.secondaryEmailBilling}) no tiene un formato válido.`);
    }

    // Preparar destinatarios principales
    const toRecipients = [
      {
        email: primaryEmail.trim(),
        name: client.company,
      },
    ];

    // Agregar email secundario si existe y es válido
    if (client.secondaryEmailBilling && client.secondaryEmailBilling.trim() !== '') {
      toRecipients.push({
        email: client.secondaryEmailBilling.trim(),
        name: `${client.company} (Email Secundario)`,
      });
    }

    // Validar tamaño del PDF (límite de ~20MB para attachments)
    const pdfSize = pdfBase64.length * 0.75 / 1024 / 1024; // Convertir de base64 a MB aproximado

    if (pdfSize > 18) { // Dejamos margen de seguridad (límite es 20MB)
      throw new Error(
        `El archivo PDF es demasiado grande para enviar por email (${pdfSize.toFixed(2)} MB). ` +
        `El límite es de 20 MB. Por favor, contacte al cliente directamente o reduzca el número de órdenes.`
      );
    }

    // Extraer el contenido base64 (remover el prefijo data:application/pdf;base64, si existe)
    let pdfContent = pdfBase64;
    if (pdfBase64.includes(',')) {
      pdfContent = pdfBase64.split(",")[1];
    }

    const emailData = {
      replyTo: {
        email: "dpaez@support-ti.com.ar",
        name: "Daniel Paez",
      },
      to: toRecipients,
      bcc: [
        {
          email: "facturas@support-ti.com.ar",
          name: "Facturas Support TI",
        },
      ],
      subject: "Detalle de Ordenes de Trabajo",
      htmlContent: `<html><body><p>Estimado ${
        client.company
      }</p><p>A continuación adjuntamos el detalle de sus órdenes de trabajo facturadas${
        facturaNumero ? ` (Factura Nro: ${facturaNumero})` : ""
      }.</p><br><p>Atte.</p><p>Equipo Support TI</p></body></html>`,
      attachment: [
        {
          content: pdfContent,
          name: facturaNumero
            ? `orden_de_trabajo_factura_${facturaNumero}.pdf`
            : "orden_de_trabajo.pdf",
        },
      ],
    };

    try {
      const response = await axios.post(
        sendEmailEndpoint,
        emailData,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data.success) {
        throw new Error(response.data.error || "Error desconocido al enviar el email");
      }
    } catch (error) {
      // Capturar información adicional del error de axios
      let errorMessage = error.message;
      if (error.response) {
        // El servidor respondió con un código de estado fuera del rango 2xx
        if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data && error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      
      throw new Error("Error al enviar el email: " + errorMessage);
    }
  };

  const createBillData = (combinedDetails, totalFinal, customExpirationDate = null, facturaEnUSD = false) => {
    const moneda = facturaEnUSD ? "DOL" : "PES";
    const cotizacion = facturaEnUSD
      ? parseFloat(selectedOrdersForFacturation[0]?.currencyRate) || 1
      : 1;
    return {
    fecha: getLocalDate(false),
    tipo:
      client.ivaCondition === "RI" || client.ivaCondition === "M"
        ? `FACTURA A`
        : `FACTURA B`,
    operacion: "V",
    idioma: 1,
    punto_venta: "00005",
    moneda,
    cotizacion,
    vencimiento: (() => {
      if (!customExpirationDate) {
        return getLocalDate(true);
      }
      const formattedDate = formatCustomDate(customExpirationDate);
      // Si formatCustomDate retorna null o una cadena vacía, usar la fecha por defecto
      return formattedDate && formattedDate.trim() !== '' ? formattedDate : getLocalDate(true);
    })(),
    periodo_facturado_desde: getLocalDate(false),
    periodo_facturado_hasta: getLocalDate(false),
    rubro: "IT TECNOLOGIA",
    rubro_grupo_contable: "",
    detalle: combinedDetails,
    total: totalFinal,
  };
  };

  const createClientData = (client) => ({
    documento_tipo: "CUIT",
    documento_nro: client.cuit,
    razon_social: client.company,
    email: [
      client.emailBilling,
      client.secondaryEmailBilling,
      "facturas@support-ti.com.ar",
    ]
      .filter(Boolean)
      .join(","),
    domicilio: client.address,
    provincia: client.province,
    envia_por_mail: "S",
    condicion_pago: 201,
    condicion_iva: client.ivaCondition,
    rg5329: "N",
  });

  const saveTicketData = async (newBill, client, combinedDetails) => {
    const { data: ticketData, error: ticketError } = await supabase
      .from("tickets")
      .insert([
        {
          clientId: client.clientId,
          ticketDate: new Date(),
          ticketNumber: newBill.comprobante_nro,
          ticketLetter: client.ivaCondition,
          ticketType: newBill.comprobante_tipo,
          ticketCae: newBill.cae,
          status: ticketObjectStatus.pendiente,
        },
      ])
      .select("*");

    if (ticketError)
      throw new Error("Error subiendo el ticket: " + ticketError.message);

    if (!ticketData || !ticketData.length)
      throw new Error("No se pudo obtener el ticket creado");

    const ticketDetails = combinedDetails.map((detail) => {
      const c = Number(detail.currency);
      const currency =
        c === currencyValues[1].value ? currencyValues[1].value : currencyValues[0].value;
      return {
        ticketId: ticketData[0].ticketId,
        quantity: detail.cantidad,
        description: detail.producto.descripcion,
        price: detail.producto.precio_unitario_sin_iva,
        ivaPercentage: detail.producto.alicuota,
        currency,
      };
    });

    const { error: ticketDetailError } = await supabase
      .from("ticketDetails")
      .insert(ticketDetails);

    if (ticketDetailError)
      throw new Error(
        "Error subiendo el detalle del ticket: " + ticketDetailError.message
      );

    return ticketData[0].ticketId;
  };

  const updateOrderStatus = async (selectedOrdersForFacturation, ticketId) => {
    const orderIds = selectedOrdersForFacturation.map((obj) => obj.orderId);

    const { error: errorOrder } = await supabase
      .from("orders")
      .update({
        status: orderObjectStatus.facturada,
        ticketId: ticketId,
      })
      .in("orderId", orderIds);

    if (errorOrder)
      throw new Error("Error actualizando la orden: " + errorOrder.message);
  };

  const updateTicketStatus = async (ticketId, status) => {
    const { error: ticketStatusError } = await supabase
      .from("tickets")
      .update({ status })
      .eq("ticketId", ticketId);

    if (ticketStatusError)
      throw new Error(
        "Error actualizando el estado del ticket: " + ticketStatusError.message
      );
  };

  /** True si todas las órdenes tienen todos sus detalles en USD (currency !== 1). */
  const allDetailsAreUSD = (orders) => {
    if (!orders?.length) return false;
    for (const order of orders) {
      const details = order.orderDetails;
      if (!details?.length) return false;
      for (const d of details) {
        const c = d.currency;
        if (c === 1 || c === "1") return false;
      }
    }
    return true;
  };

  // Función helper para obtener orderIds únicos
  const getUniqueOrderIds = (orders) => {
    const uniqueOrderIds = [...new Set(orders.map(obj => obj.orderId))];
    return uniqueOrderIds.join(',');
  };

  // Función helper para obtener órdenes únicas sin duplicados
  const getUniqueOrders = (orders) => {
    const uniqueOrders = [];
    const seenOrderIds = new Set();
    
    orders.forEach(order => {
      if (!seenOrderIds.has(order.orderId)) {
        seenOrderIds.add(order.orderId);
        uniqueOrders.push(order);
      }
    });
    
    return uniqueOrders;
  };

  const handleSaveFacturedOrders = async () => {
    const systemStatus = await checkSystemStatus();
    if (!systemStatus) {
      ErrorModal({
        title: "Error!",
        text: "El sistema está bloqueado para Facturar, no se generan facturas, ya que otro proceso está pendiente.",
      });
      return;
    }

    // Validar que exista la fecha de vencimiento
    if (!customExpirationDate || customExpirationDate.trim() === '') {
      ErrorModal({
        title: "Error",
        text: "Falta completar la fecha de vencimiento",
      });
      return;
    }

    // Validar que la fecha de vencimiento sea mayor o igual a hoy (usando zona horaria local)
    if (!validateExpirationDate(customExpirationDate)) {
      // Obtener fecha de hoy para el mensaje
      const today = getTodayLocalDate();
      const todayStr = `${String(today.getDate()).padStart(2, "0")}/${String(today.getMonth() + 1).padStart(2, "0")}/${today.getFullYear()}`;
      
      ErrorModal({
        title: "Error de Validación",
        text: `La fecha de vencimiento debe ser mayor o igual a la fecha actual (${todayStr}). Por favor, corrija la fecha antes de continuar.`,
      });
      return;
    }
    try {
      setLoading(true);
      //Bloqueo el sistema para que no se pueda facturar otra factura hasta que se termine la actual
      updateSystemStatus(false);

      // Primero creamos la factura para obtener el número
      const uniqueOrders = getUniqueOrders(selectedOrdersForFacturation);
      const facturaEnUSD = allDetailsAreUSD(uniqueOrders);

      const combinedDetails = uniqueOrders.flatMap((obj) =>
        obj.orderDetails.map((detail) => {
          const currencyMultiplier = facturaEnUSD
            ? 1
            : detail.currency !== currencyValues[0].value
              ? parseFloat(obj.currencyRate)
              : 1;

          return {
            cantidad: detail.quantity || 1,
            afecta_stock: "N",
            currency: detail.currency,
            producto: {
              descripcion:
                "[Orden Nro: " + obj.orderId + "] " + detail.description,
              unidad_bulto: 1,
              lista_precios: "",
              precio_unitario_sin_iva: (
                parseFloat(detail.price) * currencyMultiplier
              ).toFixed(2),
              alicuota: detail.ivaPercentage === 1 ? 0 : detail.ivaPercentage,
              unidad_medida: 7,
              actualiza_precio: "N",
              rg5329: "N",
            },
          };
        })
      );

      const subtotalSinIVA = combinedDetails.reduce(
        (acc, detail) =>
          acc +
          parseFloat(detail.producto.precio_unitario_sin_iva) * detail.cantidad,
        0
      );

      const totalIVA = combinedDetails.reduce(
        (acc, detail) =>
          acc +
          parseFloat(detail.producto.precio_unitario_sin_iva) *
            detail.cantidad *
            (detail.producto.alicuota / 100),
        0
      );

      const totalFinal = parseFloat(subtotalSinIVA + totalIVA).toFixed(2);

      const billData = createBillData(combinedDetails, totalFinal, customExpirationDate, facturaEnUSD);
      const clientData = createClientData(client);
      let newBill = {};

      if (!client.test) {
        // Obtener los orderIds únicos para logging
        const orderIds = getUniqueOrderIds(selectedOrdersForFacturation);
        
        newBill = await createNewBill(billData, clientData, null, orderIds);
        if (newBill.error === "S") {
          throw new Error(newBill.errores);
        }
      } else {
        newBill = {
          error: "N",
          errores: [],
          error_cod: [],
          error_details: [],
          external_reference: "",
          requiere_fec: "NO",
          observaciones:
            " Observacion: El credito fiscal discriminado en el presente comprobante solo podra ser computado a efectos del Procedimiento permanente de transicion al Regimen General. [ codigo: 10217 ].",
          tfc_generacion_tipo: 6,
          rta:
            "El comprobante FACTURA X 00001-" +
            selectedOrdersForFacturation[0].orderId
              .toString()
              .padStart(8, "0") +
            " (PAEZ RAUL DANIEL) se ha guardado correctamente ",
          cae:
            "00001" +
            selectedOrdersForFacturation[0].orderId.toString().padStart(8, "0"),
          vencimiento_cae: new Date(
            new Date().setMonth(new Date().getMonth() + 10)
          )
            .toLocaleDateString("es-AR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            .replace(/\//g, "/"),
          vencimiento_pago: new Date(
            new Date().setMonth(new Date().getMonth() + 1)
          )
            .toLocaleDateString("es-AR", {
              day: "2-digit",
              month: "2-digit",
              year: "numeric",
            })
            .replace(/\//g, "/"),
          comprobante_nro:
            "00001-" +
            selectedOrdersForFacturation[0].orderId.toString().padStart(8, "0"),
          comprobante_tipo: "FACTURA X",
          afip_codigo_barras: " ",
          afip_qr: "",
          micrositios: {
            descarga: "",
            cliente: "",
          },
          envio_x_mail: "N",
        };
      }

      if (newBill.error === "S") {
        throw new Error(newBill.errores);
      } else {
        // Primero guardamos los datos del ticket
        const ticketId = await saveTicketData(newBill, client, combinedDetails);

        // Actualizamos el estado de la orden con el ticketId
        await updateOrderStatus(
          uniqueOrders,
          ticketId
        );

        // Actualizamos el estado del ticket
        await updateTicketStatus(
          ticketId,
          ticketObjectStatus.autorizada
        );

        // Generamos y enviamos el PDF
        const facturaNumero = newBill.comprobante_nro;
        const pdfBase64 = await generatePDF(
          uniqueOrders,
          facturaNumero
        );
        
        // Intentar enviar el correo - si falla, registrar pero no bloquear el proceso
        try {
          await sendEmail(pdfBase64, facturaNumero);
        } catch (emailError) {
          // Log del error de envío de correo
          try {
            const orderIds = getUniqueOrderIds(selectedOrdersForFacturation);
            await logError(
              emailError,
              "sendEmail - Error al enviar correo de factura",
              ticketId,
              orderIds,
              facturaNumero
            );
          } catch (logError) {
            // Error logging handled silently
          }
          
          // Enviar notificación de error de correo
          try {
            await sendErrorNotification(
              emailError,
              `sendEmail - Error al enviar correo de factura ${facturaNumero}`
            );
          } catch (notificationError) {
            // Error notification handled silently
          }
          
          // Mostrar advertencia pero no bloquear el proceso
          ErrorModal({
            title: "Advertencia - Correo no enviado",
            text: `La factura ${facturaNumero} se creó correctamente, pero hubo un error al enviar el correo al cliente. 
Error: ${emailError && emailError.stack ? emailError.stack : emailError.message}. 
Por favor, envíe el correo manualmente.`,
          });
        }

        resetTotals();
        setSelectedOrdersForFacturation([]);
        getOrders();
        //Desbloqueo el sistema para que se pueda facturar otra factura si no ha ocurrido un error
        updateSystemStatus(true);
      }
    } catch (error) {
      // Log error
      try {
        const orderIds = getUniqueOrderIds(selectedOrdersForFacturation);
        await logError(
          error,
          "handleSaveFacturedOrders",
          null,
          orderIds,
          null
        );
      } catch (logError) {
        // Error logging handled silently
      }
      
      // Send error notification email
      try {
        await sendErrorNotification(error, "handleSaveFacturedOrders");
      } catch (emailError) {
        // Email error handled silently
      }
      
      // Desbloquear el sistema en caso de error
      try {
        updateSystemStatus(true);
      } catch (unlockError) {
        // Error al desbloquear manejado silenciosamente
        console.error("Error al desbloquear el sistema:", unlockError);
      }
      
      ErrorModal({
        title: "Error inesperado",
        text: error.message,
      });
    } finally {
      setLoading(false);
    }
  };

  return loading ? (
    <div
      style={{
        width: "100%",
        height: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <LoaderSpinner />
    </div>
  ) : (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%", minWidth: 0 }}>
      <ClientInfo client={client} />

      {/* La fecha rige la factura que se emite, así que va antes de elegir órdenes. */}
      <FormCard
        title="Fecha de vencimiento"
        subtitle="Es la que va a llevar la factura de las órdenes seleccionadas."
      >
        <Box sx={{ maxWidth: 300 }}>
          <Field error={expirationDateError}>
            <TextInput
              id="expiration-date"
              type="date"
              invalid={Boolean(expirationDateError)}
              min={new Date().toISOString().split('T')[0]}
              value={customExpirationDate || ''}
              onChange={(e) => {
                const value = e.target.value || null;
                setCustomExpirationDate(value);
                if (value) {
                  validateExpirationDate(value);
                }
              }}
            />
          </Field>
        </Box>
      </FormCard>

      <FinishedOrders 
        client={client} 
        finishedOrders={finishedOrders} 
        isLoading={loadingDetails}
      />
      <ReadyForBillOrders
        readyForBillOrders={readyForBillOrders}
        client={client}
        selectedOrdersForFacturation={selectedOrdersForFacturation}
        setSelectedOrdersForFacturation={setSelectedOrdersForFacturation}
        setTotalExpenses={setTotalExpenses}
        setTotalExpensesWithIva={setTotalExpensesWithIva}
        setTotalIva105={setTotalIva105}
        setTotalIva210={setTotalIva210}
        isLoading={loadingDetails}
      />
      <Totals
        totalExpenses={totalExpenses}
        totalExpensesWithIva={totalExpensesWithIva}
        iva105={totalIva105}
        iva21={totalIva210}
      />
      <FormActions>
        <Button variant="outlined" sx={{ minHeight: 44 }} onClick={() => navigate(-1)}>
          Volver
        </Button>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          onClick={handleSaveFacturedOrders}
          disabled={selectedOrdersForFacturation.length === 0}
          sx={{ minHeight: 44, flexGrow: { xs: 1, md: 0 }, minWidth: 220 }}
        >
          {selectedOrdersForFacturation.length === 0
            ? "Facturar órdenes seleccionadas"
            : `Facturar ${selectedOrdersForFacturation.length} ${
                selectedOrdersForFacturation.length === 1 ? "orden" : "órdenes"
              }`}
        </Button>
      </FormActions>
    </Box>
  );
};

export default ClientSpecificOrdersTable;
